import { line, DEAL_VALUE_SOURCE_HELP, Size, dealTimingHasTime, formatDateOnly, formatDateTime, t, type DealTimingDates, type DealValueSource, type StringKey } from '@bms2/shared';
import { HelpButton } from '../../components/modals/HelpModal';
import { Pill } from '../../components/controls';
import { MoneyText } from '../../components/Money';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import sharedStyles from '../../components/composites/shared.module.css';
import styles from './DealBannerColumn.module.css';

/**
 * 🔒 The Deal banner's summary column (decision 0665, amending 0621 and 0334):
 * the Deal value between the name and the status pills, the page's own
 * formatted figure. Next Action stood beside it, three lines of the list with the
 * rest counted, until decision 1219 made it the page's own banner. 🔒 The Timing
 * row's dates read beside the value where any is set and the banner has the room
 * (decision 0719).
 */
const BANNER_TIMING: readonly { key: keyof DealTimingDates; label: StringKey }[] = [
  { key: 'expectedQuoteDate', label: 'deal.timing.quoteShort' },
  { key: 'expectedStartDate', label: 'deal.timing.startShort' },
  { key: 'expectedFinishDate', label: 'deal.timing.finishShort' },
];

/**
 * 🔒 The value carries the Budget Too Low pill in line with its title (decision
 * 0726, as decision 1219 shrank it), and the information it came from reads only
 * inside the ? help beside the figure (decision 1219); the floor and the cost are
 * never figures here. 🔒 Next Action left this column for the page's own banner
 * (decision 1219).
 */
export function DealBannerColumn({ valueText, valueSource = null, budgetTooLow = false, timing = null }: { valueText: string | null; valueSource?: DealValueSource | null; budgetTooLow?: boolean; timing?: DealTimingDates | null }) {
  const format = useDateFormat();
  const zone = useViewerZone();
  const dates = timing ? BANNER_TIMING.filter((field) => timing[field.key] !== null) : [];
  return (
    <div className={styles.column} data-deal-banner-column="true">
      <div className={styles.cell} data-deal-banner-value="true">
        <span className={styles.titleLine}>
          <span className={styles.label}>{t('deal.banner.value')}</span>
          {budgetTooLow ? (
            <span className={styles.warning} data-deal-banner-budget-warning="true">
              <Pill text={t('deal.banner.budgetTooLow')} foreground="var(--status-error-text)" background="var(--status-error-bg)" size={Size.Small} />
            </span>
          ) : null}
        </span>
        <span className={styles.valueLine}>
          {/* 🔨 A banner figure is a string the server already formatted, so a negative inside it is found in its text (decision 0481). */}
          <span className={styles.value}>{valueText ? <MoneyText text={valueText} /> : '—'}</span>
          {valueText ? (
            <span className={styles.help} data-deal-banner-value-help={valueSource ?? ''}>
              <HelpButton topic="dealValue" detail={valueSource ? t(DEAL_VALUE_SOURCE_HELP[valueSource]) : null} />
            </span>
          ) : null}
        </span>
      </div>
      {/* 🔒 The Timing row's dates, where any is set and the banner has the room: the cell folds away below md and the card carries them alone (decision 0719). */}
      {timing && dates.length > 0 ? (
        <div className={`${styles.cell} ${styles.timing}`} data-deal-banner-timing={String(dates.length)}>
          <span className={styles.label}>{t('deal.banner.timing')}</span>
          <ul className={`${sharedStyles.listReset} ${styles.lines}`}>
            {dates.map((field) => (
              <li key={field.key} className={styles.line} data-deal-banner-timing-row={field.key}>{line(t(field.label), dealTimingHasTime(timing[field.key]!) ? formatDateTime(timing[field.key]!, zone, format) : formatDateOnly(timing[field.key]!, format))}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
